/**
 * Hook for real-time game events via WebSocket room subscription.
 *
 * Replaces direct SSE client usage in game screens. Subscribes to
 * room:subscribe and routes game:event and game:room_updated messages
 * to the provided handlers.
 */

import { useEffect, useRef, useState } from "react";
import type { GameRoom } from "../game-client/types";
import { wsActions } from "../store/ws-store";

type ConnectionStatus = "connected" | "reconnecting";

interface GameEventCallbacks {
  readonly onGameEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
    userId: string,
  ) => void;
  readonly onRoomUpdated: (room: GameRoom) => void;
  readonly onReconnect?: () => void;
  /**
   * ADR-04: per-recipient projected ViewerState push (hidden-information
   * games — Celestia). The server already filtered this for the
   * authenticated user; the client renders it verbatim, never a raw
   * GameState. Non-hidden games never receive this message.
   */
  readonly onViewerState?: (
    state: Record<string, unknown>,
    seq: number,
  ) => void;
}

interface UseGameEventsWSResult {
  readonly connectionStatus: ConnectionStatus;
}

export function useGameEventsWS(
  roomId: string | null,
  callbacks: GameEventCallbacks,
): UseGameEventsWSResult {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("reconnecting");

  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!roomId) return;

    let hasReceivedFirstMessage = false;

    const unsubscribeRoom = wsActions.subscribeRoom(roomId, (message) => {
      if (!hasReceivedFirstMessage) {
        hasReceivedFirstMessage = true;
        setConnectionStatus("connected");
      }

      if (message.type === "game:event") {
        callbacksRef.current.onGameEvent(
          message.eventType,
          message.payload,
          message.seq,
          message.userId,
        );
      }
      if (message.type === "game:room_updated") {
        callbacksRef.current.onRoomUpdated(message.room);
      }
      if (message.type === "game:viewer_state") {
        callbacksRef.current.onViewerState?.(message.state, message.seq);
      }
    });

    // Listen for reconnection events to trigger event replay
    const unsubscribeConnection = wsActions.onConnectionChange((status) => {
      if (status === "connected" && hasReceivedFirstMessage) {
        // WS reconnected — trigger replay to catch missed events
        callbacksRef.current.onReconnect?.();
      }
      setConnectionStatus(
        status === "connected" ? "connected" : "reconnecting",
      );
    });

    return () => {
      unsubscribeRoom();
      unsubscribeConnection();
    };
  }, [roomId]);

  return { connectionStatus };
}
